import { useState } from 'react';
import { formatDateTime } from '../../../lib/locale';
import { useTranslation } from 'react-i18next';
import { Lock, LockOpen } from 'lucide-react';
import { useMe } from '../../../shell/me';
import { useAsync } from '../../../hooks/useAsync';
import { ErrorBox, errorMessageKey } from '../../../pages/ErrorBox';
import { toastStore } from '../../../state/toasts';
import { sharingApi } from '../api';
import { formatBytes } from './WorkspaceSettingsPage';

function Stats() {
  const { t, i18n } = useTranslation();
  const stats = useAsync('admin:stats', () => sharingApi().adminStats());
  if (stats.status === 'loading') return <p className="muted">{t('common.loading')}</p>;
  if (stats.status === 'error') return <ErrorBox error={stats.error} onRetry={stats.reload} />;
  const s = stats.data;
  const cards: [string, string][] = [
    ['users', s.users.toLocaleString(i18n.language)],
    ['workspaces', s.workspaces.toLocaleString(i18n.language)],
    ['boards', s.boards.toLocaleString(i18n.language)],
    ['activeLinks', s.activeLinks.toLocaleString(i18n.language)],
    ['storage', formatBytes(s.storageBytes, i18n.language)],
  ];
  return (
    <section aria-labelledby="admin-stats">
      <h2 id="admin-stats">{t('sharing.admin.stats')}</h2>
      <dl className="card-grid stat-grid">
        {cards.map(([k, v]) => (
          <div key={k} className="card stat">
            <dt className="muted small">{t(`sharing.admin.${k}`)}</dt>
            <dd className="stat-value">{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function LockUser() {
  const { t, i18n } = useTranslation();
  const [id, setId] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const run = (locked: boolean) => {
    const userId = id.trim();
    if (userId === '') return;
    sharingApi()
      .lockUser(userId, locked)
      .then(
        (r) =>
          setResult(
            r.lockedAt
              ? t('sharing.admin.locked', {
                  id: r.id,
                  date: formatDateTime(r.lockedAt, i18n.language),
                })
              : t('sharing.admin.unlocked', { id: r.id }),
          ),
        (err: unknown) =>
          toastStore.getState().push({ kind: 'error', message: t(errorMessageKey(err)) }),
      );
  };
  return (
    <section className="settings-section" aria-labelledby="admin-lock">
      <h2 id="admin-lock">{t('sharing.admin.lockTitle')}</h2>
      <p className="muted small">{t('sharing.admin.lockHint')}</p>
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          run(true);
        }}
      >
        <label className="field">
          <span>{t('sharing.admin.userId')}</span>
          <input value={id} onChange={(e) => setId(e.target.value)} maxLength={64} />
        </label>
        <button type="submit" className="btn btn-danger" disabled={id.trim() === ''}>
          <Lock size={15} aria-hidden="true" /> {t('sharing.admin.lock')}
        </button>
        <button
          type="button"
          className="btn"
          disabled={id.trim() === ''}
          onClick={() => run(false)}
        >
          <LockOpen size={15} aria-hidden="true" /> {t('sharing.admin.unlock')}
        </button>
      </form>
      {result && <p role="status">{result}</p>}
    </section>
  );
}

/** `/admin` – platform admins only (stats, lock user). */
export function AdminPage() {
  const { t } = useTranslation();
  const me = useMe();
  if (me.status === 'loading') return <p className="page muted">{t('common.loading')}</p>;
  if (me.status !== 'ready' || !me.me.platformAdmin) {
    return (
      <div className="page">
        <h1>{t('admin.title')}</h1>
        <p role="alert">{t('errors.forbidden')}</p>
      </div>
    );
  }
  return (
    <div className="page">
      <h1>{t('admin.title')}</h1>
      <Stats />
      <LockUser />
    </div>
  );
}
